import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../../util/axiosBaseQuery";
import { AssetGroup } from "../../types/assetGroup";
import type { UseCase } from "../../util/useCases";
import type { LocationPros } from "../../types/baseTypes";
import { ObjectSearchResult, withCategoryNotice } from "../apartment/categoryOutcome";

export const processAssetGroupData = (assetGroupData: any): AssetGroup => {
  return {
    id: assetGroupData.id,
    name: assetGroupData.name,
    langNames: assetGroupData.langNames,
    description: assetGroupData.description,
    limit: assetGroupData.limit,
    availabilities: assetGroupData.availibility,
    isFullDay: assetGroupData.isFullDay,
  };
};

export const processAssetGroupsData = (
  assetGroupsData: any[]
): AssetGroup[] => {
  return assetGroupsData.map(
    (assetGroupData: any): AssetGroup => processAssetGroupData(assetGroupData)
  );
};

export const locationsApiSlice = createApi({
  reducerPath: "locationsApiSlice",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["locationList", "locationITems", "locationItemsList", "BookingDetailsByEmail", "BookingDetailsByToken"],
  endpoints: (builder) => ({
    getLocationList: builder.query({
      query: (queryParam) => {
        // Updated to not accept any arguments
        return {
          url: queryParam ? `/location?services=${queryParam}` : `/location`,
        };
      },
      providesTags: ["locationList"],
    }),

    /**
     * #3429 step 3: the location list of a station flow entered from a
     * category tile — only the locations that offer that category (PO
     * 2026-10-30). A separate endpoint, not an argument of `getLocationList`:
     * every caller reads that one's data as a plain list, and this one also
     * carries the server's category notice (archived / unknown, the room
     * search's `X-Product-Category-Outcome`). The key is part of the argument,
     * so two category tiles never share a cache entry.
     */
    getCategoryLocationList: builder.query<
      ObjectSearchResult<LocationPros[]>,
      { services: string; productCategory: string }
    >({
      query: ({ services, productCategory }) => ({
        url: `/location?services=${encodeURIComponent(services)}&product_category=${encodeURIComponent(productCategory)}`,
      }),
      transformResponse: (items: LocationPros[], meta) => withCategoryNotice(items, meta),
      providesTags: ["locationList"],
    }),

    getLocationDetails: builder.query({
      query: ({ locationId }) => {
        return {
          url: `/location/${locationId}`,
        };
      },
      providesTags: ["locationList"],
    }),

    getLocationEquipment: builder.query({
      query: ({ locationId, types, productCategory }: { locationId: string; types?: string; productCategory?: string }) => {
        // #3429 step 3: a category tile narrows the station's products the
        // way it narrows the room search. Part of the argument, so the cache
        // keeps one entry per category.
        const params = new URLSearchParams();
        if (types) params.set("types", types);
        if (productCategory) params.set("product_category", productCategory);
        const query = params.toString();
        const url = `/location/${locationId}/groups/include-price`;
        return { url: query ? `${url}?${query}` : url };
      },
      providesTags: ["locationITems"],
    }),

    getAssetGroupsByLocationIdsList: builder.query({
      query: ({ locationIds }: { locationIds: string[] }) => {
        return {
          url: `/locations/${locationIds.toString()}/groups`,
        };
      },
      transformResponse: (response: any) => {
        return processAssetGroupsData(response);
      },
      providesTags: ["locationList"],
    }),

    getDepotByLocationId: builder.query({
      query: ({ locationId }: { locationId: string }) => {
        return {
          url: `/location/${locationId}/depot`,
        };
      },
      providesTags: ["locationList"],
    }),
    getLocationItemsByLocationId: builder.query({
      query: ({ locationId }: { locationId: string }) => {
        return {
          url: `/location/${locationId}/groups/include-price`,
        };
      },
      providesTags: ["locationItemsList"],
    }),
    getBookingDetailsByEmail: builder.query({
      query: ({email}) => ({
        url: `/orders/email/${email}`,
      }),
      providesTags: ["BookingDetailsByEmail"],
    }),
    getBookingDetailsByToken: builder.query({
      query: ({token}) => ({
        url: `/orders/bookings/${token}`,
      }),
      providesTags: ["BookingDetailsByToken"],
    }),
    getMaxGuests: builder.query<
      Record<string, number>,
      { useCase?: UseCase } | void
    >({
      // Each flow scopes the cap to its own use case, mirroring the
      // object search's scoping — another family's assets must not set it.
      query: (args) => ({
        url: args?.useCase
          ? `location/guests/max?use_case=${args.useCase}`
          : "location/guests/max",
      }),
    }),
  }),
});

export const {
  useGetLocationListQuery,
  useGetCategoryLocationListQuery,
  useGetLocationEquipmentQuery,
  useGetLocationDetailsQuery,
  useGetLocationItemsByLocationIdQuery,
  useGetAssetGroupsByLocationIdsListQuery,
  useGetDepotByLocationIdQuery,
  useLazyGetBookingDetailsByEmailQuery,
  useLazyGetBookingDetailsByTokenQuery,
    useGetMaxGuestsQuery
} = locationsApiSlice;
